import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { toast } from 'react-hot-toast';
import {
  Users,
  Search,
  Filter,
  UserCheck,
  UserX,
  Shield,
  Mail,
  Trash2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  User,
  Edit
} from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout.jsx';

// GraphQL Queries y Mutations
const GET_ALL_USERS = gql`
  query GetAllUsers {
    users {
      id
      email
      full_name
      is_active
      role {
        id
        role_name
      }
      belongs_to
      parent {
        id
        full_name
      }
      created_at
    }
    roles {
      id
      role_name
    }
  }
`;

const UPDATE_USER_APPROVAL = gql`
  mutation UpdateUserApproval($id: UUID!, $is_active: Boolean!) {
    updateUser(id: $id, input: { is_active: $is_active }) {
      id
      email
      full_name
      is_active
    }
  }
`;

const ASSIGN_USER_ROLE = gql`
  mutation AssignUserRole($id: UUID!, $roleId: UUID) {
    updateUser(id: $id, input: { roleId: $roleId }) {
      id
      email
      full_name
      role {
        id
        role_name
      }
      belongs_to
      parent {
        id
        full_name
      }
    }
  }
`;

const UPDATE_USER_BELONGS_TO = gql`
  mutation UpdateUserBelongsTo($id: UUID!, $belongs_to: UUID!) {
    updateUser(id: $id, input: { belongs_to: $belongs_to }) {
      id
      email
      full_name
      belongs_to
      parent {
        id
        full_name
      }
    }
  }
`;

const DELETE_USER = gql`
  mutation DeleteUser($id: UUID!) {
    deleteUser(id: $id)
  }
`;

const UPDATE_USER_PROFILE = gql`
  mutation UpdateUserProfile($id: UUID!, $email: String, $full_name: String) {
    updateUser(id: $id, input: { email: $email, full_name: $full_name }) {
      id
      email
      full_name
    }
  }
`;

const UsersManagementPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'unapproved'
  const [selectedUser, setSelectedUser] = useState(null);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [isBelongsToModalOpen, setIsBelongsToModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [editFormData, setEditFormData] = useState({ email: '', full_name: '' });

  // Queries
  const { data, loading, error, refetch } = useQuery(GET_ALL_USERS, {
    fetchPolicy: 'cache-and-network'
  });

  // Mutations
  const [updateUserApproval] = useMutation(UPDATE_USER_APPROVAL, {
    onCompleted: (data) => {
      const status = data.updateUser.is_active ? 'aprobado' : 'desaprobado';
      const emailAction = data.updateUser.is_active ? 'aprobación' : 'desactivación';
      toast.success(
        `Usuario ${status} exitosamente. Se ha enviado un email de ${emailAction} al usuario.`,
        { duration: 4000 }
      );
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const [assignUserRole] = useMutation(ASSIGN_USER_ROLE, {
    onCompleted: () => {
      toast.success('Rol asignado exitosamente');
      setIsRoleModalOpen(false);
      setSelectedUser(null);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const [updateUserBelongsTo] = useMutation(UPDATE_USER_BELONGS_TO, {
    onCompleted: () => {
      toast.success('Relación actualizada exitosamente');
      setIsBelongsToModalOpen(false);
      setSelectedUser(null);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const [deleteUser] = useMutation(DELETE_USER, {
    onCompleted: () => {
      toast.success('Usuario eliminado exitosamente');
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const [updateUserProfile] = useMutation(UPDATE_USER_PROFILE, {
    onCompleted: () => {
      toast.success('Usuario actualizado exitosamente');
      setIsEditModalOpen(false);
      setSelectedUser(null);
      setEditFormData({ email: '', full_name: '' });
      refetch();
    },
    onError: (error) => {
      // Manejo específico de error de email duplicado
      if (error.message.includes('unique') || error.message.includes('Unique constraint')) {
        toast.error('Este email ya está registrado. Por favor usa otro email.');
      } else {
        toast.error(error.message);
      }
    }
  });

  // Filtros y búsqueda
  const filteredUsers = useMemo(() => {
    if (!data?.users) return [];
    
    let filtered = data.users;

    // Filtro por estado de aprobación
    if (filterType === 'unapproved') {
      filtered = filtered.filter(user => !user.is_active);
    }

    // Búsqueda por email y full_name
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.email.toLowerCase().includes(searchLower) ||
        user.full_name.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [data?.users, filterType, searchTerm]);

  // Obtener usuarios con rol Cliente O Transportista para belongs_to
  const clientUsers = useMemo(() => {
    return data?.users?.filter(user =>
      user.role?.role_name === 'Cliente' || user.role?.role_name === 'Transportista'
    ) || [];
  }, [data?.users]);

  // Filtrar clientes/transportistas por búsqueda
  const filteredClientUsers = useMemo(() => {
    if (!clientSearchTerm.trim()) return clientUsers;

    const searchLower = clientSearchTerm.toLowerCase();
    return clientUsers.filter(client =>
      client.full_name.toLowerCase().includes(searchLower) ||
      client.email.toLowerCase().includes(searchLower)
    );
  }, [clientUsers, clientSearchTerm]);

  // Handlers
  const handleApprovalToggle = (user) => {
    const action = user.is_active ? 'desaprobar' : 'aprobar';
    const message = user.is_active 
      ? `¿Deseas desaprobar a ${user.full_name}?\n\nSe enviará automáticamente un email de notificación informando que su cuenta ha sido desactivada y las instrucciones para reactivarla.\n\nImportante: Se eliminarán automáticamente todos los roles y relaciones asignados al usuario.`
      : `¿Deseas aprobar a ${user.full_name}?\n\nSe enviará automáticamente un email de bienvenida con la información de acceso al sistema y sus credenciales de inicio de sesión.`;

    setConfirmAction({
      type: 'approval',
      user,
      action,
      message,
      onConfirm: () => updateUserApproval({
        variables: { 
          id: user.id, 
          is_active: !user.is_active 
        }
      })
    });
  };

  const handleRoleAssignment = (user) => {
    if (!user.is_active) {
      toast.error('No se puede asignar rol a un usuario no aprobado. Primero debe aprobar al usuario.');
      return;
    }
    setSelectedUser(user);
    setIsRoleModalOpen(true);
  };

  const handleRoleChange = (user) => {
    if (!user.is_active) {
      toast.error('No se puede cambiar el rol de un usuario no aprobado. Primero debe aprobar al usuario.');
      return;
    }
    setSelectedUser(user);
    setIsRoleModalOpen(true);
  };

  const handleBelongsToUpdate = (user) => {
    if (!user.is_active) {
      toast.error('No se puede modificar la relación de un usuario no aprobado. Primero debe aprobar al usuario.');
      return;
    }
    if (user.role?.role_name !== 'Notificado') {
      toast.error('Solo usuarios con rol "Notificado" pueden tener relación belongs_to');
      return;
    }
    setSelectedUser(user);
    setClientSearchTerm(''); // Reset search when opening modal
    setIsBelongsToModalOpen(true);
  };

  const handleDeleteUser = (user) => {
    // TODO: Validar transacciones en Shipment/Shipment_details
    setConfirmAction({
      type: 'delete',
      user,
      action: 'eliminar',
      message: `¿Estás seguro de que deseas eliminar a ${user.full_name}? Esta acción no se puede deshacer.`,
      onConfirm: () => deleteUser({ variables: { id: user.id } })
    });
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditFormData({
      email: user.email,
      full_name: user.full_name
    });
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();

    // Validaciones
    if (!editFormData.full_name.trim() || editFormData.full_name.trim().length < 3) {
      toast.error('El nombre debe tener al menos 3 caracteres');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editFormData.email.trim())) {
      toast.error('Por favor ingresa un email válido');
      return;
    }

    // Verificar si hubo cambios
    if (editFormData.email === selectedUser.email && editFormData.full_name === selectedUser.full_name) {
      toast.error('No se detectaron cambios');
      return;
    }

    updateUserProfile({
      variables: {
        id: selectedUser.id,
        email: editFormData.email.trim(),
        full_name: editFormData.full_name.trim()
      }
    });
  };

  const handleRoleSubmit = (roleId) => {
    if (selectedUser) {
      assignUserRole({
        variables: {
          id: selectedUser.id,
          roleId: roleId // Can be null for "Ninguno"
        }
      });
    }
  };

  const handleBelongsToSubmit = (clientId) => {
    if (selectedUser && clientId) {
      updateUserBelongsTo({
        variables: {
          id: selectedUser.id,
          belongs_to: clientId
        }
      });
    }
  };

  if (error) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error al cargar los usuarios: {error.message}</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
            <Users className="h-6 w-6 mr-3 text-blue-600" />
            Administración de usuarios
          </h1>
          <p className="text-gray-600 mb-4">
            Gestiona los usuarios del sistema, sus roles y permisos
          </p>
          
          {/* Información adicional para el usuario */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Información:</h4>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• <strong>Aprobar Usuario:</strong> Al presionar "Por aprobar", se activa la cuenta y se envía automáticamente un email de bienvenida con credenciales de acceso.</li>
              <li>• <strong>Desaprobar Usuario:</strong> Al presionar "Aprobado", se desactiva la cuenta, se eliminan sus roles y relaciones, y se envía automáticamente un email informando sobre la desactivación.</li>
              <li>• <strong>Roles:</strong> Solo se pueden asignar o cambiar roles en usuarios aprobados. Haga clic en el nombre del rol para cambiarlo. Solo usuarios con rol "Notificado" pueden ser asignados a un cliente.</li>
              <li>• <strong>Emails Automáticos:</strong> Todos los cambios de estado envían notificaciones automáticas por email al usuario afectado.</li>
            </ul>
          </div>
        </div>

        {/* Filtros y búsqueda */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Búsqueda */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por email o nombre..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            {/* Botones de filtro */}
            <div className="flex gap-2">
              <button
                onClick={() => setFilterType('unapproved')}
                className={`bg-yellow-100 border-yellow-200 px-4 py-2 rounded-lg font-medium transition-colors flex items-center ${
                  filterType === 'unapproved'
                    ? ' text-yellow-800 border'
                    : ' text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                <UserX className="h-4 w-4 mr-2" />
                Mostrar usuarios no aprobados
              </button>
              <button
                onClick={() => setFilterType('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center ${
                  filterType === 'all'
                    ? 'bg-blue-100 text-blue-800 border border-blue-200'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                <Users className="h-4 w-4 mr-2" />
                Mostrar todos los usuarios
              </button>
            </div>
          </div>
        </div>

        {/* Tabla de usuarios */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Cargando usuarios...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No se encontraron usuarios</p>
              {searchTerm && <p className="text-gray-400 text-sm mt-2">Intenta ajustar tu búsqueda</p>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuario
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rol
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pertenece a
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      {/* Usuario */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                            <User className="h-5 w-5 text-gray-600" />
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                            <button
                              onClick={() => handleEditUser(user)}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Editar usuario"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </td>

                      {/* Email */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Mail className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900">{user.email}</span>
                        </div>
                      </td>

                      {/* Rol */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.role ? (
                          <button
                            onClick={() => handleRoleChange(user)}
                            disabled={!user.is_active}
                            className={`inline-block w-24 text-center px-1 py-1 text-xs font-semibold rounded-full transition-colors ${
                              user.is_active 
                                ? 'bg-blue-100 text-blue-800 hover:bg-blue-200 underline cursor-pointer'
                                : 'bg-gray-100 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            {user.role.role_name}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleRoleAssignment(user)}
                            disabled={!user.is_active}
                            className={`w-24 px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                              user.is_active
                                ? 'text-orange-700 bg-orange-100 border border-orange-300 hover:bg-orange-200 cursor-pointer'
                                : 'text-gray-500 bg-gray-100 border border-gray-300 cursor-not-allowed'
                            }`}
                          >
                            {user.is_active ? 'Asignar rol' : 'Sin rol'}
                          </button>
                        )}
                      </td>

                      {/* Belongs To */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.role?.role_name === 'Notificado' ? (
                          user.parent ? (
                            <button
                              onClick={() => handleBelongsToUpdate(user)}
                              disabled={!user.is_active}
                              className={`text-sm transition-colors ${
                                user.is_active
                                  ? 'text-blue-600 hover:text-blue-800 underline cursor-pointer'
                                  : 'text-gray-400 cursor-not-allowed'
                              }`}
                            >
                              {user.parent.full_name}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleBelongsToUpdate(user)}
                              disabled={!user.is_active}
                              className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                                user.is_active
                                  ? 'bg-red-100 text-red-800 hover:bg-red-200 cursor-pointer'
                                  : 'bg-gray-100 text-gray-500 cursor-not-allowed'
                              }`}
                            >
                              <XCircle className="inline h-3 w-3 mr-1" />
                              {user.is_active ? 'Sin asignar' : 'Sin relación'}
                            </button>
                          )
                        ) : (
                          <span className="text-sm text-gray-500">No aplica</span>
                        )}
                      </td>

                      {/* Estado (is_approved) */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleApprovalToggle(user)}
                          className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                            user.is_active
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          }`}
                        >
                          {user.is_active ? (
                            <>
                              <CheckCircle className="inline h-3 w-3 mr-1" />
                              Aprobado
                            </>
                          ) : (
                            <>
                              <XCircle className="inline h-3 w-3 mr-1" />
                              Por aprobar
                            </>
                          )}
                        </button>
                      </td>

                      {/* Delete */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar usuario"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal de confirmación */}
        {confirmAction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-md w-full mx-4 p-6">
              <div className="flex items-center mb-4">
                <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Confirmar {confirmAction.action}
                </h3>
              </div>
              <p className="text-gray-600 mb-6 whitespace-pre-line">{confirmAction.message}</p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setConfirmAction(null)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    confirmAction.onConfirm();
                    setConfirmAction(null);
                  }}
                  className={`px-4 py-2 text-white rounded-lg ${
                    confirmAction.type === 'delete' 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-primary-600 hover:bg-primary-700'
                  }`}
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de asignación de rol */}
        {isRoleModalOpen && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {selectedUser.role ? 'Cambiar rol de' : 'Asignar rol a'} {selectedUser.full_name}
              </h3>
              {selectedUser.role && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Rol actual:</strong> {selectedUser.role.role_name}
                  </p>
                  <p className="text-xs text-yellow-600 mt-1">
                    Si selecciona un rol diferente a "Notificado", se eliminará automáticamente la relación "Pertenece a".
                  </p>
                </div>
              )}
              <div className="space-y-2 mb-6">
                {/* Opción Ninguno */}
                <button
                  onClick={() => handleRoleSubmit(null)}
                  className={`w-full text-left px-3 py-2 border rounded-lg transition-colors ${
                    !selectedUser.role
                      ? 'border-gray-400 bg-gray-50 text-gray-800'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <XCircle className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-gray-600 italic">Ninguno</span>
                    </div>
                    {!selectedUser.role && (
                      <span className="text-xs text-gray-600 font-medium">Actual</span>
                    )}
                  </div>
                </button>
                
                {/* Roles existentes */}
                {data?.roles?.map((role) => (
                  <button
                    key={role.id}
                    onClick={() => handleRoleSubmit(role.id)}
                    className={`w-full text-left px-3 py-2 border rounded-lg transition-colors ${
                      selectedUser.role?.id === role.id
                        ? 'border-blue-300 bg-blue-50 text-blue-800'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Shield className="h-4 w-4 text-gray-400 mr-2" />
                        <span>{role.role_name}</span>
                      </div>
                      {selectedUser.role?.id === role.id && (
                        <span className="text-xs text-blue-600 font-medium">Actual</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setIsRoleModalOpen(false);
                    setSelectedUser(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de edición */}
        {isEditModalOpen && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col">
              {/* Header fijo */}
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Editar usuario: {selectedUser.full_name}
                </h3>
              </div>

              {/* Contenido - Formulario */}
              <form onSubmit={handleEditSubmit} className="flex-1 overflow-y-auto p-6">
                <div className="space-y-4">
                  {/* Campo Nombre completo */}
                  <div>
                    <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre completo *
                    </label>
                    <input
                      type="text"
                      id="full_name"
                      value={editFormData.full_name}
                      onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Ingresa el nombre completo"
                      required
                    />
                    {editFormData.full_name.trim().length > 0 && editFormData.full_name.trim().length < 3 && (
                      <p className="text-xs text-red-600 mt-1">El nombre debe tener al menos 3 caracteres</p>
                    )}
                  </div>

                  {/* Campo Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Correo electrónico *
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="usuario@ejemplo.com"
                      required
                    />
                  </div>

                  {/* Nota informativa */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-700">
                      <strong>Nota:</strong> Los cambios en el email no afectan las transacciones existentes, ya que todas las relaciones se basan en el ID del usuario.
                    </p>
                  </div>
                </div>
              </form>

              {/* Footer fijo */}
              <div className="p-6 border-t border-gray-200">
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setSelectedUser(null);
                      setEditFormData({ email: '', full_name: '' });
                    }}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleEditSubmit}
                    disabled={
                      !editFormData.full_name.trim() ||
                      editFormData.full_name.trim().length < 3 ||
                      !editFormData.email.trim() ||
                      (editFormData.email === selectedUser.email && editFormData.full_name === selectedUser.full_name)
                    }
                    className={`px-4 py-2 text-white rounded-lg ${
                      !editFormData.full_name.trim() ||
                      editFormData.full_name.trim().length < 3 ||
                      !editFormData.email.trim() ||
                      (editFormData.email === selectedUser.email && editFormData.full_name === selectedUser.full_name)
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-primary-600 hover:bg-primary-700'
                    }`}
                  >
                    Guardar cambios
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de belongs_to */}
        {isBelongsToModalOpen && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col">
              {/* Header fijo */}
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Asignar relación a {selectedUser.full_name}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Seleccione un Cliente o Transportista al que pertenece este usuario
                </p>

                {/* Campo de búsqueda */}
                {clientUsers.length > 0 && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar por nombre o email..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      value={clientSearchTerm}
                      onChange={(e) => setClientSearchTerm(e.target.value)}
                    />
                  </div>
                )}
              </div>

              {/* Contenido con scroll */}
              <div className="flex-1 overflow-y-auto p-6">
                {clientUsers.length === 0 ? (
                  <p className="text-gray-500">No hay usuarios con rol "Cliente" o "Transportista" disponibles</p>
                ) : filteredClientUsers.length === 0 ? (
                  <div className="text-center py-4">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No se encontraron usuarios</p>
                    <p className="text-gray-400 text-sm mt-1">Intenta ajustar tu búsqueda</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredClientUsers.map((client) => (
                      <button
                        key={client.id}
                        onClick={() => handleBelongsToSubmit(client.id)}
                        className="w-full text-left px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center">
                          <User className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">{client.full_name}</div>
                            <div className="text-sm text-gray-500 truncate">{client.email}</div>
                            <div className="text-xs text-blue-600 font-medium mt-0.5">
                              {client.role?.role_name || 'Sin rol'}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer fijo */}
              <div className="p-6 border-t border-gray-200">
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setIsBelongsToModalOpen(false);
                      setSelectedUser(null);
                      setClientSearchTerm('');
                    }}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default UsersManagementPage;